/**
 * Полум'я: the picture of a flame, painted once.
 *
 * "Зроби щоб був видимий вогонь - зараз просто блоки мерехтять і змінюють
 * колір, над блоком має бути полум'я." What the game had was a translucent
 * orange cube wrapped round the burning block, which is exactly what that
 * describes: the block went orange and pulsed.
 *
 * A flame is not a shape a voxel world has. It is a picture, and this is the
 * picture: a tongue of fire, wide and white at the foot, narrowing and
 * cooling as it goes up, with a notch or two out of its edges so it does not
 * read as a leaf. The renderer stands two of these up crossed over the
 * burning block, which is how every game of this kind draws one.
 *
 * Pure: a rectangle of bytes. It has never heard of a texture.
 */

export const FLAME_W = 16;
export const FLAME_H = 24;

/**
 * Two of these, and they are not the same.
 *
 * A pair of crossed billboards cut from one picture is a cross of the same
 * flame, which reads as a cross. Painted from a seed each, the two halves
 * gutter differently and the thing looks like fire from any angle.
 */
export function paintFlame(seed = 1): Uint8ClampedArray {
  const px = new Uint8ClampedArray(FLAME_W * FLAME_H * 4);
  const noise = (x: number, y: number): number => {
    const n = Math.sin((x * 12.9898 + y * 78.233 + seed * 37.719) * 43758.5453);
    return n - Math.floor(n);
  };

  for (let y = 0; y < FLAME_H; y++) {
    /*
     * How far up the flame this row is, and how wide it is there.
     *
     * Widest a third of the way up rather than at the very bottom: a flame
     * pinches where it meets what it is burning, bellies out, and tapers to
     * the tip. A straight triangle reads as a party hat.
     */
    const up = y / (FLAME_H - 1);
    /*
     * Row nought is the foot of the flame, which is how a texture is read.
     *
     * Widest a quarter of the way up and a point at the tip: a flame pinches
     * where it meets what it is burning, bellies out, and tapers. The first
     * draft peaked three quarters of the way up and came out a balloon on a
     * stick.
     */
    const half = Math.sin(Math.pow(up, 0.55) * Math.PI) * (FLAME_W / 2 - 0.6) +
      (1 - up) * 0.8;
    for (let x = 0; x < FLAME_W; x++) {
      const from = Math.abs(x + 0.5 - FLAME_W / 2);
      // A bite out of the edge here and there, so the outline wavers.
      const edge = half - noise(x, y) * (0.6 + 2.2 * up);
      if (from > edge) continue;

      /*
       * White at the heart, orange in the body, red at the edge and the tip.
       *
       * The heat is how deep inside the flame this pixel is *and* how low
       * down it is - the top of a flame is the coolest part of it, which is
       * what makes a tongue of fire read as rising rather than as a blob.
       */
      const deep = 1 - from / Math.max(0.6, edge);
      const heat = Math.max(0, Math.min(1, deep * 1.2 - up * 0.75));
      const i = (y * FLAME_W + x) * 4;
      if (heat > 0.66) {
        px[i] = 255; px[i + 1] = 242; px[i + 2] = 186;
      } else if (heat > 0.34) {
        px[i] = 252; px[i + 1] = 176; px[i + 2] = 52;
      } else {
        px[i] = 238; px[i + 1] = 112; px[i + 2] = 32;
      }
      /*
       * Fading out at the very top, so the flame ends in smokeless air
       * rather than in a cut edge.
       */
      px[i + 3] = Math.round(255 * Math.min(1, (1 - up) * 3.2 + 0.15));
    }
  }
  return px;
}
